import { useState } from 'react';
import { useNavigate } from 'react-router';
import { formatEuro, formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Avatar, Banner, Button, Pill, Skeleton, TextLink } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { CreateFinancingDialog } from '../../financings/create/CreateFinancingDialog';
import { ReassignDialog } from '../../financings/dialogs/ReassignDialog';
import { StagePill } from '../../financings/ui/StagePill';
import { useDealFinancings, useInvalidateDeals } from '../api';
import type { DealFinancingsResponse, DealRecord } from '../types';
import { NextStepCard, ParticipantsCard, SideCard } from './bits';
import styles from './record.module.css';
import own from './financings.module.css';

/**
 * Deal-Akte 05/06 — the financings of the property deal (`GET /deals/:id/financings`). With financings (05, also
 * the lost-only deal of Finanzierungen 12): the list, the preview of the first active financing (financier, banks)
 * and the gate card while none is signed; sidebar „Aus der Selbstauskunft" + „Beteiligte". Without any financing
 * (06): the empty state with the matching candidates; sidebar „Recht", „Nächster Schritt", „Beteiligte".
 * „Finanzierung anlegen" and „Vorschlagen" open the create dialog of the financings module.
 */

type CreateState = { financierId?: string | undefined } | null;

function dash(value: string | null): string {
  return value ?? '—';
}

function money(cents: number | null): string {
  return cents === null ? '—' : formatEuro(cents);
}

function ListCard({ data, onCreate }: { data: DealFinancingsResponse; onCreate: () => void }) {
  return (
    <section className={own.card} aria-labelledby="deal-financings-title">
      <div className={own.head}>
        <div className={own.headText}>
          <h2 id="deal-financings-title" className={own.title}>
            {data.intro.title}
          </h2>
          <p className={own.text}>{data.intro.text}</p>
        </div>
        {data.canCreate ? (
          <Button variant="secondary" size="small" onClick={onCreate}>
            Finanzierung anlegen
          </Button>
        ) : null}
      </div>
      <div className={own.list} role="list" aria-label="Finanzierungen">
        {data.items.map((item) => (
          <div key={item.id} className={own.item} role="listitem" aria-label={item.numberLabel}>
            <span className={own.number}>{item.numberLabel}</span>
            <span className={own.itemText}>
              <span className={own.itemTitle}>{item.title}</span>
              <span className={own.itemSub}>{item.financierLine}</span>
            </span>
            <span className={own.amount}>{money(item.amountCents)}</span>
            <span className={own.pillCell}>
              <StagePill stage={item.stage} />
            </span>
            <TextLink to={item.href} size="small" aria-label={`${item.numberLabel} · Akte öffnen`}>
              Akte öffnen
            </TextLink>
          </div>
        ))}
      </div>
    </section>
  );
}

function Preview({
  preview,
  onReassign,
}: {
  preview: NonNullable<DealFinancingsResponse['preview']>;
  onReassign: () => void;
}) {
  const financier = preview.financier;
  return (
    <>
      <div className={own.previewEyebrow}>Vorschau {preview.numberLabel}</div>
      {financier ? (
        <section className={clsx(own.card, own.financierCard)} aria-label={`Finanzierer ${financier.name}`}>
          <div className={own.financier}>
            <Avatar initials={financier.initials} name={financier.name} size={40} tone="navy" />
            <div className={own.financierText}>
              <div className={own.financierName}>{financier.name}</div>
              <div className={own.financierLine}>{financier.line}</div>
            </div>
            <Button
              variant="secondary"
              size="small"
              onClick={() => window.location.assign(`mailto:${financier.email}`)}
            >
              Nachricht
            </Button>
            {financier.canReassign ? (
              <Button variant="secondary" size="small" onClick={onReassign}>
                Neu zuweisen
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}
      <section className={own.card} aria-labelledby="deal-financings-banks">
        <div className={own.head}>
          <div className={own.headText}>
            <h2 id="deal-financings-banks" className={own.title}>
              Banken
            </h2>
            <p className={own.text}>{preview.banks.text}</p>
          </div>
        </div>
        {preview.banks.rows.length > 0 ? (
          <div className={own.banks} role="table" aria-labelledby="deal-financings-banks">
            <div className={own.bankHead} role="row">
              <span role="columnheader">Bank</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Konditionen</span>
              <span role="columnheader" />
            </div>
            <div className={own.bankRows}>
              {preview.banks.rows.map((row) => (
                <div key={row.id} role="row" className={clsx(own.bankRow, row.archived && own.bankArchived)}>
                  <span role="cell" className={own.bankName}>
                    {row.bank}
                  </span>
                  <span role="cell">
                    <Pill variant={row.tone}>{row.statusLabel}</Pill>
                  </span>
                  <span role="cell" className={own.bankLine}>
                    {row.line}
                  </span>
                  <span
                    role="cell"
                    className={clsx(own.bankDate, row.status === 'chosen' && own.bankDateChosen)}
                  >
                    {row.dateLabel ?? ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </section>
    </>
  );
}

function GateCard({ gate }: { gate: NonNullable<DealFinancingsResponse['gateCard']> }) {
  const navigate = useNavigate();
  return (
    <section className={clsx(own.card, own.gate)} aria-label={gate.title}>
      <span className={own.gateMark} aria-hidden="true" />
      <div className={own.gateText}>
        <div className={own.gateTitle}>{gate.title}</div>
        <div className={own.gateDetail}>{gate.text}</div>
      </div>
      <Button variant="secondary" disabled={!gate.actionAllowed} onClick={() => void navigate(gate.href)}>
        {gate.actionLabel}
      </Button>
      {!gate.actionAllowed && gate.actionHint ? (
        <span className={own.gateHint}>{gate.actionHint}</span>
      ) : null}
    </section>
  );
}

function SelfDisclosureCard({ facts }: { facts: NonNullable<DealFinancingsResponse['selfDisclosure']> }) {
  const rows: [string, string][] = [
    ['Finanzierungsbedarf', money(facts.needCents)],
    ['Eigenkapital', money(facts.equityCents)],
    [
      'Haushaltsnetto',
      facts.householdNetCents === null ? '—' : `${formatEuro(facts.householdNetCents)} / Monat`,
    ],
    ['Beschäftigung', dash(facts.employment)],
  ];
  return (
    <SideCard label="Aus der Selbstauskunft">
      <div className={own.facts}>
        {rows.map(([key, value]) => (
          <div key={key} className={own.fact}>
            <span className={own.factKey}>{key}</span>
            <span className={own.factValue}>{value}</span>
          </div>
        ))}
      </div>
      <div className={own.sideLink}>
        <TextLink to={facts.href} size="small">
          Selbstauskunft öffnen
        </TextLink>
      </div>
    </SideCard>
  );
}

function EmptyCards({
  empty,
  canCreate,
  onCreate,
}: {
  empty: NonNullable<DealFinancingsResponse['empty']>;
  canCreate: boolean;
  onCreate: (financierId?: string) => void;
}) {
  return (
    <>
      <section className={own.card} aria-labelledby="deal-financings-empty">
        <div className={own.head}>
          <div className={own.headText}>
            <h2 id="deal-financings-empty" className={own.title}>
              {empty.title}
            </h2>
            <p className={own.text}>{empty.text}</p>
          </div>
          {canCreate ? (
            <Button size="small" onClick={() => onCreate()}>
              Finanzierung anlegen
            </Button>
          ) : null}
        </div>
      </section>
      <section className={own.card} aria-labelledby="deal-financings-matching">
        <div className={own.head}>
          <div className={own.headText}>
            <h2 id="deal-financings-matching" className={own.title}>
              {empty.financierTitle}
            </h2>
            <p className={own.text}>{empty.financierText}</p>
          </div>
        </div>
        {empty.tags.length > 0 ? (
          <div className={own.tags} aria-label="Kundenprofil">
            {empty.tags.map((tag) => (
              <Pill key={tag}>{tag}</Pill>
            ))}
          </div>
        ) : null}
        {empty.candidates.length === 0 ? (
          <p className={own.candidatesEmpty}>Noch kein aktiver Finanzierer in der Organisation.</p>
        ) : (
          <div className={own.candidates} role="list" aria-label="Vorgeschlagene Finanzierer">
            {empty.candidates.map((candidate) => (
              <div
                key={candidate.userId}
                role="listitem"
                aria-label={candidate.name}
                className={clsx(own.candidate, candidate.best && own.candidateBest)}
              >
                <span className={own.candidateAvatar} aria-hidden="true">
                  {candidate.initials}
                </span>
                <span className={own.candidateText}>
                  <span className={own.candidateName}>
                    {candidate.name}
                    {candidate.best ? <Pill variant="accent">beste Übereinstimmung</Pill> : null}
                  </span>
                  <span className={own.candidateLine}>{candidate.line}</span>
                </span>
                <span className={own.candidateCases}>
                  {candidate.openCases === 1
                    ? '1 offener Fall'
                    : `${formatInteger(candidate.openCases)} offene Fälle`}
                </span>
                <span className={own.candidateMatch}>passt zu {formatInteger(candidate.matches)} von 4</span>
                {canCreate ? (
                  <Button
                    variant={candidate.best ? 'primary' : 'secondary'}
                    size={candidate.best ? 'default' : 'small'}
                    aria-label={`${candidate.name} vorschlagen`}
                    onClick={() => onCreate(candidate.userId)}
                  >
                    Vorschlagen
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

export function FinancingsTab({ deal }: { deal: DealRecord }) {
  const financings = useDealFinancings(deal.id, true);
  const invalidate = useInvalidateDeals();
  const [create, setCreate] = useState<CreateState>(null);
  const [reassign, setReassign] = useState(false);
  const data = financings.data;

  return (
    <div className={styles.content}>
      <div className={styles.main}>
        {financings.isPending ? (
          <section className={styles.card}>
            <div className={styles.skeletonStack}>
              <Skeleton width="40%" height={16} />
              <Skeleton width="80%" />
              <Skeleton width="55%" />
            </div>
          </section>
        ) : null}
        {financings.isError ? (
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void financings.refetch()}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(financings.error)}
          </Banner>
        ) : null}
        {data?.empty ? (
          <EmptyCards
            empty={data.empty}
            canCreate={data.canCreate}
            onCreate={(financierId) => setCreate({ financierId })}
          />
        ) : null}
        {data && !data.empty ? (
          <>
            <ListCard data={data} onCreate={() => setCreate({})} />
            {data.preview ? <Preview preview={data.preview} onReassign={() => setReassign(true)} /> : null}
            {data.gateCard ? <GateCard gate={data.gateCard} /> : null}
          </>
        ) : null}
      </div>
      <div className={styles.side}>
        {data?.empty ? (
          <>
            <SideCard label="Recht">
              <p className={own.sideText}>{data.empty.rightsText}</p>
            </SideCard>
            <NextStepCard deal={deal} />
          </>
        ) : null}
        {data && !data.empty && data.selfDisclosure ? (
          <SelfDisclosureCard facts={data.selfDisclosure} />
        ) : null}
        <ParticipantsCard deal={deal} />
      </div>

      {create ? (
        <CreateFinancingDialog
          dealId={deal.id}
          financierId={create.financierId}
          onClose={(created) => {
            setCreate(null);
            if (created) void invalidate(deal.id);
          }}
        />
      ) : null}
      {data?.preview ? (
        <ReassignDialog
          financingId={data.preview.financingId}
          open={reassign}
          onClose={() => setReassign(false)}
        />
      ) : null}
    </div>
  );
}
